import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Company } from '../types/Company';
import { useAuth } from '../hooks/useAuth';
import { getCompany } from '../api/company';

interface CompanyContextType {
    company: Company | null;
    isLoadingCompany: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { state: authState } = useAuth();
    const [company, setCompany] = useState<Company | null>(null);
    const [isLoadingCompany, setIsLoadingCompany] = useState(true);

    useEffect(() => {
        const fetchCompany = async (userId: string) => {
            setIsLoadingCompany(true);
            try {
                const companyData = await getCompany(userId);
                setCompany(companyData);
            } catch (error) {
                console.error("Failed to fetch company data", error);
                setCompany(null);
            } finally {
                setIsLoadingCompany(false);
            }
        };

        if (authState.isAuthenticated && authState.user) {
            fetchCompany(authState.user.id);
        } else if (!authState.isLoading) {
            setCompany(null);
            setIsLoadingCompany(false);
        }
    }, [authState.isAuthenticated, authState.user, authState.isLoading]);

    return (
        <CompanyContext.Provider value={{ company, isLoadingCompany }}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error('useCompany must be used within a CompanyProvider');
    }
    return context;
};
